import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { projectsApi } from '../lib/api';
import { useBulkDeleteProjects } from './useBulkActions';

/** A lost response is unknown, not a failed write. Read before offering another delete. */
export function useProjectDeletion(teamId?: string) {
  const mutation = useBulkDeleteProjects();
  const { getToken } = useAuth();
  const [selection, setSelection] = useState<{ ids: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsCheck, setNeedsCheck] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const scope = useRef(teamId);
  scope.current = teamId;

  useEffect(() => {
    setSelection(null);
    setError(null);
    setNeedsCheck(false);
  }, [teamId]);

  const open = (value: { ids: string[] } | null) => {
    if (pending.current) return;
    setSelection(value);
    setError(null);
    setNeedsCheck(false);
  };

  const confirm = async () => {
    if (!selection || !teamId || pending.current) return;
    const attemptedTeam = teamId;
    pending.current = true;
    setBusy(true);
    try {
      if (needsCheck) {
        const token = await getToken();
        if (!token) throw new Error('Sign in again to check these projects.');
        // This read includes archived projects and has no search/role filter.
        // An inaccessible row is not claimed deleted: it is simply excluded
        // from any further destructive request.
        const current = await projectsApi.list(token, attemptedTeam, { includeArchived: true });
        if (scope.current !== attemptedTeam) return;
        const accessible = new Set(current.map(project => project.id));
        const remaining = selection.ids.filter(id => accessible.has(id));
        if (!remaining.length) {
          setSelection(null);
          toast.info('These projects are no longer in your project list.');
        } else {
          setSelection({ ids: remaining });
          setError('These projects are still present. Review the selection, then confirm deletion again.');
        }
        setNeedsCheck(false);
        return; // A status check never issues a second delete.
      }

      const result = await mutation.mutateAsync({ ids: selection.ids });
      if (scope.current !== attemptedTeam) return;
      const deleted = new Set(result.success);
      const remaining = selection.ids.filter(id => !deleted.has(id));
      if (!remaining.length) {
        setSelection(null);
      } else {
        setSelection({ ids: remaining });
        setError('Some projects could not be deleted. Check their status before trying again.');
        setNeedsCheck(true);
      }
    } catch {
      if (scope.current !== attemptedTeam) return;
      setError(needsCheck
        ? 'Could not check the projects. Try checking again when your connection returns.'
        : 'Deletion was not confirmed. Check the projects before trying again.');
      setNeedsCheck(true);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return { selection, open, confirm, error, needsCheck, busy };
}
